import React, { useState, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  Card, 
  CardContent, 
  Button, 
  Chip, 
  useTheme,
  Stack,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  CardActionArea,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import { 
  BarChart, 
  ArrowForward, 
  Info, 
  ArrowUpward,
  ArrowDownward,
  CompareArrows,
  PieChart,
  FilterList,
  CheckCircle,
  Cancel,
  TrendingUp,
  TrendingDown,
  DragHandle,
  ShowChart,
  Warning,
  MonetizationOn,
  HelpOutline
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import { MarketReportFull, MarketReportDetails } from '@/types/investment';
import { getSignalColor } from '../../theme/themesV3';

// Define the trend data interface for each market cap
interface TrendData {
  performance: {
    performance_rating?: string;
    performance_detail?: string;
    projected_trend_short?: string;
    projected_trend_mid?: string;
    projected_trend_long?: string;
  } | null;
  risk: {
    risk_rating?: string;
    risk_detail?: string;
    projected_trend_short?: string;
    projected_trend_mid?: string;
    projected_trend_long?: string;
  } | null;
  moneyFlow: {
    money_flow_rating?: string;
    money_flow_detail?: string;
    projected_trend_short?: string;
    projected_trend_mid?: string;
    projected_trend_long?: string;
  } | null;
}

// Define interface for market cap with trend data
interface MarketCapWithTrendData extends MarketReportDetails {
  trendData: TrendData;
}

// Styled components
const MarketCapCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
  }
}));

const AllocationBar = styled(LinearProgress)<{ value: number, max: number }>(({ theme, value, max }) => ({
  height: 10,
  borderRadius: 5,
  backgroundColor: theme.palette.grey[200],
  '.MuiLinearProgress-bar': {
    borderRadius: 5,
    backgroundColor: value > max ? theme.palette.error.main : theme.palette.primary.main,
  }
}));

// New styled components for historical ratings (if not already present)
const RatingHistoryDot = styled('div')(({ color }) => ({
  width: 4,
  height: 4,
  borderRadius: '50%',
  backgroundColor: color,
  margin: '0 1px',
}));

const RatingHistoryContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: 6,
  height: 10, // Keep height consistent
});

// Helper function to get trend data for a specific market cap
const getMarketCapTrendData = (marketCapName: string, trendReports: any): TrendData => {
  if (!trendReports) return { performance: null, risk: null, moneyFlow: null };
  
  // Extract data from each trend report type
  const performance = trendReports["Performance"]?.MarketCaps?.Reports.find(
    (s: MarketReportDetails) => s.Classification.replace('Market Cap: ', '') === marketCapName || 
                               s.Classification.includes(marketCapName)
  );
  
  const risk = trendReports["Risk"]?.MarketCaps?.Reports.find(
    (s: MarketReportDetails) => s.Classification.replace('Market Cap: ', '') === marketCapName || 
                               s.Classification.includes(marketCapName)
  );
  
  const moneyFlow = trendReports["Money Flow"]?.MarketCaps?.Reports.find(
    (s: MarketReportDetails) => s.Classification.replace('Market Cap: ', '') === marketCapName || 
                               s.Classification.includes(marketCapName)
  );
  
  return { 
    performance: performance ? {
      performance_rating: performance.Rating,
      performance_detail: performance.RatingTrendExplanation,
      projected_trend_short: performance.ProjectedTrend_ShortTerm,
      projected_trend_mid: performance.ProjectedTrend_MidTerm,
      projected_trend_long: performance.ProjectedTrend_LongTerm
    } : null, 
    risk: risk ? {
      risk_rating: risk.Rating,
      risk_detail: risk.RatingTrendExplanation,
      projected_trend_short: risk.ProjectedTrend_ShortTerm,
      projected_trend_mid: risk.ProjectedTrend_MidTerm,
      projected_trend_long: risk.ProjectedTrend_LongTerm
    } : null, 
    moneyFlow: moneyFlow ? {
      money_flow_rating: moneyFlow.Rating,
      money_flow_detail: moneyFlow.RatingTrendExplanation,
      projected_trend_short: moneyFlow.ProjectedTrend_ShortTerm,
      projected_trend_mid: moneyFlow.ProjectedTrend_MidTerm,
      projected_trend_long: moneyFlow.ProjectedTrend_LongTerm
    } : null
  };
};

// Helper component for trend chip
const TrendChip = ({ label, value, tooltip }: { label: string, value: string | undefined, tooltip?: string }) => {
  if (!value) return null;
  
  let color = getSignalColor(value);
  let icon = <Info fontSize="small" />;

  if (label.includes('Performance')) {
     icon = <ShowChart fontSize="small" />;
  } else if (label.includes('Risk')) {
    icon = <Warning fontSize="small" />;
  } else if (label.includes('Money Flow')) {
    icon = <MonetizationOn fontSize="small" />;
  } else {
    icon = <Info fontSize="small" />;
  }
  
  const chip = (
    <Chip
      size="medium"
      icon={icon}
      label={`${label}: ${value}`}
      sx={{
        bgcolor: `${color}20`,
        color: color,
        borderColor: color,
        fontSize: '0.65rem',
        height: 22,
        '& .MuiChip-icon': {
          color: color,
          fontSize: '0.75rem',
        }
      }}
      variant="outlined"
    />
  );
  
  if (tooltip) {
    return (
      <Tooltip title={tooltip} arrow>
        {chip}
      </Tooltip>
    );
  }
  
  return chip;
};


// Define props
interface MarketCapsViewProps {
  marketReport: MarketReportFull;
  onTickerSelect?: (ticker: string) => void;
  renderMarketRating: (rating: string) => React.ReactElement;
  renderMarketTrend: (trend: string) => React.ReactElement;
  onTabChange: (index: number, subtab?: number) => void;
}

const MarketCapsView: React.FC<MarketCapsViewProps> = ({ 
  marketReport, 
  onTickerSelect,
  renderMarketRating,
  renderMarketTrend,
  onTabChange
}) => {
  const [filter, setFilter] = useState<string | null>('all');
  const [selectedMarketCap, setSelectedMarketCap] = useState<MarketCapWithTrendData | null>(null);
  const theme = useTheme();
  
  const report = marketReport?.market_report;
  const trendReports = marketReport?.trend_reports;
  const historicalReports = marketReport?.historical_reports || {}; // Get historical reports
  
  // Initialize marketCapReports before potential early return
  const marketCapReports = report?.MarketCaps?.Reports || [];
  
  // Memoize market cap data with trend information - moved BEFORE the early return
  const marketCapsWithTrendData = useMemo(() => {
    return marketCapReports.map((marketCap: MarketReportDetails) => {
      // Extract the simple market cap name without "Market Cap: " prefix
      const marketCapName = marketCap.Classification.replace('Market Cap: ', '');
      const trendData = getMarketCapTrendData(marketCapName, trendReports);
      
      return {
        ...marketCap,
        trendData
      } as MarketCapWithTrendData;
    });
  }, [marketCapReports, trendReports]);
  
  // Check if report exists - moved AFTER the hooks are defined
  if (!report) {
    return (
      <Alert severity="warning">
        No market report data available. Please refresh or try again later.
      </Alert>
    );
  }

  const handleFilterChange = (
    event: React.MouseEvent<HTMLElement>,
    newFilter: string | null,
  ) => {
    if (newFilter !== null) {
      setFilter(newFilter);
    }
  };

  // Filter market caps based on selected filter
  const filteredMarketCaps = () => {
    if (filter === 'all') return marketCapsWithTrendData;
    if (filter === 'bullish') return marketCapsWithTrendData.filter((s: MarketCapWithTrendData) => s.Rating.includes('BULLISH'));
    if (filter === 'bearish') return marketCapsWithTrendData.filter((s: MarketCapWithTrendData) => s.Rating.includes('BEARISH'));
    if (filter === 'neutral') {
      return marketCapsWithTrendData.filter((s: MarketCapWithTrendData) => 
        !s.Rating.includes('BULLISH') && !s.Rating.includes('BEARISH')
      );
    }
    return marketCapsWithTrendData;
  };

  // Helper function to get trend icon and color
  const getTrendIconAndColor = (trend: string) => {
    if (!trend) {
      return { icon: <Info fontSize="small" />, color: theme.palette.grey[500] };
    }
    
    const trendLower = trend.toLowerCase();
    if (trendLower.includes('improving')) {
      return { icon: <ArrowUpward fontSize="small" sx={{ color: getSignalColor(trend), mr: 0.5 }} />, color: getSignalColor(trend) };
    } else if (trendLower.includes('deteriorating')) {
      return { icon: <ArrowDownward fontSize="small" sx={{ color: getSignalColor(trend), mr: 0.5 }} />, color: getSignalColor(trend) };
    } else if (trendLower.includes('bullish') || trendLower.includes('moderately bullish')) {
      return { icon: <TrendingUp fontSize="small" sx={{ color: getSignalColor(trend), mr: 0.5 }} />, color: getSignalColor(trend) };
    } else if (trendLower.includes('neutral') || trendLower.includes('steady')) {
      return { icon: <CompareArrows fontSize="small" sx={{ color: getSignalColor(trend), mr: 0.5 }} />, color: getSignalColor(trend) };
    } else if (trendLower.includes('bearish') || trendLower.includes('moderately bearish')) {
      return { icon: <TrendingDown fontSize="small" sx={{ color: getSignalColor(trend), mr: 0.5 }} />, color: getSignalColor(trend) };
    } else {
      return { icon: <HelpOutline fontSize="small" sx={{ color: theme.palette.grey[500], mr: 0.5 }} />, color: theme.palette.grey[500] };
    }
  };

  const handleMarketCapSelect = (marketCap: MarketCapWithTrendData) => {
    setSelectedMarketCap(marketCap);
  };

  // Helper function to render historical rating trend for a specific market cap
  const renderHistoricalMarketCapRating = (marketCapClassification: string) => {
    if (!historicalReports || Object.keys(historicalReports).length === 0) return null;
    
    // Get dates in ascending order (oldest to newest)
    const dates = Object.keys(historicalReports).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    
    // Take up to 20 most recent dates
    const recentDates = dates.slice(Math.max(0, dates.length - 20));
    
    return (
      <RatingHistoryContainer>
        {recentDates.map((date) => {
          const histReport = historicalReports[date]?.market_report?.MarketCaps?.Reports;
          if (!histReport) return null;
          
          // Find the matching market cap in historical data
          const capMatch = histReport.find(
            (m: any) => m.Classification === marketCapClassification
          );
          
          if (!capMatch || !capMatch.Rating) return null;
          
          return (
            <Tooltip 
              key={date} 
              title={`${formatDate(date)}: ${capMatch.Rating}`}
              arrow
              placement="top"
            >
              <RatingHistoryDot color={getSignalColor(capMatch.Rating)} />
            </Tooltip>
          );
        })}
      </RatingHistoryContainer>
    );
  };

  // Helper function to render historical trend data for specific market cap trend report
  const renderHistoricalTrendRating = (reportType: 'Performance' | 'Risk' | 'Money Flow') => {
    if (!historicalReports || Object.keys(historicalReports).length === 0 || !selectedMarketCap) return null;
    
    // Get dates in ascending order (oldest to newest)
    const dates = Object.keys(historicalReports).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    
    // Take up to 20 most recent dates
    const recentDates = dates.slice(Math.max(0, dates.length - 20));
    
    return (
      <RatingHistoryContainer>
        {recentDates.map((date) => {
          const histReport = historicalReports[date]?.[reportType]?.MarketCaps?.Reports;
          if (!histReport) return null;
          
          // Find the matching market cap in historical data
          const capMatch = histReport.find(
            (m: any) => m.Classification === selectedMarketCap.Classification
          );
          
          if (!capMatch || !capMatch.Rating) return null;
          
          return (
            <Tooltip 
              key={date} 
              title={`${formatDate(date)}: ${capMatch.Rating}`}
              arrow
              placement="top"
            >
              <RatingHistoryDot color={getSignalColor(capMatch.Rating)} />
            </Tooltip>
          );
        })}
      </RatingHistoryContainer>
    );
  };

  // Helper function to format date
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString + "T00:00:00");
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (e) {
      return dateString;
    }
  };

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Market Cap Analysis
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Analysis of market performance by capitalization size: Large Cap, Mid Cap, and Small Cap.
        </Typography>
      </Box>
      
      {/* Filter controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FilterList sx={{ mr: 1, color: theme.palette.primary.main }} />
          <Typography variant="subtitle1" fontWeight="medium">
            Filter Market Caps:
          </Typography>
        </Box>
        
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={handleFilterChange}
          aria-label="market cap filter"
          size="small"
        >
          <ToggleButton value="all" aria-label="all market caps">
            All
          </ToggleButton>
          <ToggleButton value="bullish" aria-label="bullish market caps">
            <ArrowUpward fontSize="small" sx={{ mr: 0.5, color: getSignalColor('BULLISH') }} />
            Bullish
          </ToggleButton>
          <ToggleButton value="neutral" aria-label="neutral market caps">
            <CompareArrows fontSize="small" sx={{ mr: 0.5, color: getSignalColor('NEUTRAL') }} />
            Neutral
          </ToggleButton>
          <ToggleButton value="bearish" aria-label="bearish market caps">
            <ArrowDownward fontSize="small" sx={{ mr: 0.5, color: getSignalColor('BEARISH') }} />
            Bearish
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      
      <Grid container spacing={3}>
        {/* Market Cap list */}
        <Grid item xs={12} md={selectedMarketCap ? 5 : 12}>
          <Grid container spacing={2}>
            {filteredMarketCaps().map((marketCap: MarketCapWithTrendData, index: number) => {
              const { icon: trendIcon, color: trendColor } = getTrendIconAndColor(marketCap.Trend);
              const { performance, risk, moneyFlow } = marketCap.trendData || {};
              
              return (
                <Grid item xs={12} sm={selectedMarketCap ? 12 : 6} md={selectedMarketCap ? 12 : 4} key={index}>
                  <MarketCapCard 
                    elevation={2}
                    sx={{ 
                      borderLeft: `4px solid ${getSignalColor(marketCap.Rating)}`,
                      bgcolor: selectedMarketCap?.Classification === marketCap.Classification 
                        ? `${getSignalColor(marketCap.Rating)}10` 
                        : 'inherit'
                    }}
                  >
                    <CardActionArea onClick={() => handleMarketCapSelect(marketCap)}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {marketCap.Classification.replace('Market Cap: ', '')}
                          </Typography>
                          
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Chip 
                              label={marketCap.Rating}
                              size="small"
                              sx={{ 
                                bgcolor: getSignalColor(marketCap.Rating),
                                color: 'white',
                                fontWeight: 'bold'
                              }}
                            />
                            {/* Add historical rating in non-expanded view */}
                            {Object.keys(historicalReports).length > 0 && renderHistoricalMarketCapRating(marketCap.Classification)}
                          </Box>
                        </Box>
                        
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Trend:
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {trendIcon}
                            <Typography 
                              variant="body2" 
                              sx={{ ml: 0.5, color: trendColor, fontWeight: 'medium' }}
                            >
                              {marketCap.Trend}
                            </Typography>
                          </Box>
                        </Box>
                        
                        {/* Trend Metrics Section */}
                        <Box sx={{ mt: 2, mb: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Market Factors:
                          </Typography>
                          <Stack direction="column" spacing={0.5}>
                            {performance && (
                              <TrendChip 
                                label="Performance" 
                                value={performance.performance_rating} 
                                tooltip={performance.performance_detail || "Performance rating based on price action, relative strength, and momentum"}
                              />
                            )}
                            {risk && (
                              <TrendChip 
                                label="Risk" 
                                value={risk.risk_rating} 
                                tooltip={risk.risk_detail || "Risk rating based on volatility, drawdown potential, and market conditions"}
                              />
                            )}
                            {moneyFlow && (
                              <TrendChip 
                                label="Money Flow" 
                                value={moneyFlow.money_flow_rating}
                                tooltip={moneyFlow.money_flow_detail || "Money flow rating based on institutional buying/selling pressure and volume patterns"}
                              />
                            )}
                          </Stack>
                        </Box>
                        
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Recommended Allocation:
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="body2" sx={{ mr: 1, fontWeight: 'bold' }}>
                              {marketCap.Allocations?.ideal || 0}%
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              (Range: {marketCap.Allocations?.min || 0}% - {marketCap.Allocations?.max || 0}%)
                            </Typography>
                          </Box>
                          <AllocationBar 
                            variant="determinate" 
                            value={marketCap.Allocations?.ideal || 0} 
                            max={marketCap.Allocations?.max || 10}
                          />
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </MarketCapCard>
                </Grid>
              );
            })}
          </Grid>
        </Grid>
        
        {/* Market Cap detail - REARRANGED */}
        {selectedMarketCap && (
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 3, height: '100%' }}>
              {/* 1. Title and Rating */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                  <DragHandle sx={{ mr: 1, color: theme.palette.primary.main }} />
                  {selectedMarketCap.Classification.replace('Market Cap: ', '')}
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Chip 
                    label={selectedMarketCap.Rating}
                    size="small"
                    sx={{ 
                      bgcolor: getSignalColor(selectedMarketCap.Rating),
                      color: 'white',
                      fontWeight: 'bold',
                      px: 1
                    }}
                  />
                  {/* Add historical rating trend under the rating chip */}
                  {Object.keys(historicalReports).length > 0 && renderHistoricalMarketCapRating(selectedMarketCap.Classification)}
                </Box>
              </Box>
              
              {/* 2. Trend and Allocation */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Current Trend
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {renderMarketTrend(selectedMarketCap.Trend)}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Allocation Recommendation
                      </Typography>
                      <Box>
                        <Typography variant="h6" fontWeight="bold" color="primary">
                          {selectedMarketCap.Allocations?.ideal || 0}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Range: {selectedMarketCap.Allocations?.min || 0}% - {selectedMarketCap.Allocations?.max || 0}%
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              
              {/* 3. Projected Trend */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Projections:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Paper sx={{ p: 1.5, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Short-term
                      </Typography>
                      <Chip 
                        label={selectedMarketCap.ProjectedTrend_ShortTerm}
                        size="small"
                        sx={{
                          bgcolor: getSignalColor(selectedMarketCap.ProjectedTrend_ShortTerm || ''),
                          color: 'white',
                          fontWeight: 'bold'
                        }}
                      />
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper sx={{ p: 1.5, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Mid-term
                      </Typography>
                      <Chip 
                        label={selectedMarketCap.ProjectedTrend_MidTerm}
                        size="small"
                        sx={{
                          bgcolor: getSignalColor(selectedMarketCap.ProjectedTrend_MidTerm || ''),
                          color: 'white',
                          fontWeight: 'bold'
                        }}
                      />
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper sx={{ p: 1.5, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Long-term
                      </Typography>
                      <Chip 
                        label={selectedMarketCap.ProjectedTrend_LongTerm}
                        size="small"
                        sx={{
                          bgcolor: getSignalColor(selectedMarketCap.ProjectedTrend_LongTerm || ''),
                          color: 'white',
                          fontWeight: 'bold'
                        }}
                      />
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
              
              {/* 4. Analysis Section - MOVED UP */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Analysis:
                </Typography>
                <Paper sx={{ p: 2, bgcolor: theme.palette.background.paper }}>
                  <ReactMarkdown>
                    {selectedMarketCap.RatingTrendExplanation || 'No detailed analysis available.'}
                  </ReactMarkdown>
                </Paper>
              </Box>
              
              {/* 5. Trend Reports (one per row) - RENAMED */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Market Factor Analysis:
                </Typography>
                
                {/* Performance (full width) */}
                {selectedMarketCap.trendData?.performance && (
                  <Card variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                          Performance
                        </Typography>
                        <Box>
                          <Chip 
                            label={selectedMarketCap.trendData.performance.performance_rating || "N/A"}
                            size="small"
                            sx={{
                              bgcolor: getSignalColor(selectedMarketCap.trendData.performance.performance_rating || ''),
                              color: 'white',
                              fontWeight: 'bold'
                            }}
                          />
                          {/* Add historical performance trend */}
                          {Object.keys(historicalReports).length > 0 && renderHistoricalTrendRating('Performance')}
                        </Box>
                      </Box>
                      
                      <Typography variant="body2" sx={{ mb: 1.5 }}>
                        {selectedMarketCap.trendData.performance.performance_detail || "No detailed performance data available."}
                      </Typography>
                      
                      {/* Performance Projections (inline) */}
                      {(selectedMarketCap.trendData.performance.projected_trend_short || 
                        selectedMarketCap.trendData.performance.projected_trend_mid ||
                        selectedMarketCap.trendData.performance.projected_trend_long) && (
                        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          <Typography variant="body2" color="text.secondary" fontWeight="medium">
                            Projections:
                          </Typography>
                          
                          {selectedMarketCap.trendData.performance.projected_trend_short && (
                            <Chip 
                              label={`Short: ${selectedMarketCap.trendData.performance.projected_trend_short}`}
                              size="small"
                              sx={{
                                bgcolor: getSignalColor(selectedMarketCap.trendData.performance.projected_trend_short || ''),
                                color: 'white',
                                fontWeight: 'bold',
                                height: 20,
                                fontSize: '0.65rem'
                              }}
                            />
                          )}
                          
                          {selectedMarketCap.trendData.performance.projected_trend_mid && (
                            <Chip 
                              label={`Mid: ${selectedMarketCap.trendData.performance.projected_trend_mid}`}
                              size="small"
                              sx={{
                                bgcolor: getSignalColor(selectedMarketCap.trendData.performance.projected_trend_mid || ''),
                                color: 'white',
                                fontWeight: 'bold',
                                height: 20,
                                fontSize: '0.65rem'
                              }}
                            />
                          )}
                          
                          {selectedMarketCap.trendData.performance.projected_trend_long && (
                            <Chip 
                              label={`Long: ${selectedMarketCap.trendData.performance.projected_trend_long}`}
                              size="small"
                              sx={{
                                bgcolor: getSignalColor(selectedMarketCap.trendData.performance.projected_trend_long || ''),
                                color: 'white',
                                fontWeight: 'bold',
                                height: 20,
                                fontSize: '0.65rem'
                              }}
                            />
                          )}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                )}
                
                {/* Risk (full width) */}
                {selectedMarketCap.trendData?.risk && (
                  <Card variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                          Risk
                        </Typography>
                        <Box>
                          <Chip 
                            label={selectedMarketCap.trendData.risk.risk_rating || "N/A"}
                            size="small"
                            sx={{
                              bgcolor: getSignalColor(selectedMarketCap.trendData.risk.risk_rating || ''),
                              color: 'white',
                              fontWeight: 'bold'
                            }}
                          />
                          {/* Add historical risk trend */}
                          {Object.keys(historicalReports).length > 0 && renderHistoricalTrendRating('Risk')}
                        </Box>
                      </Box>
                      
                      <Typography variant="body2" sx={{ mb: 1.5 }}>
                        {selectedMarketCap.trendData.risk.risk_detail || "No detailed risk data available."}
                      </Typography>
                      
                      {/* Risk Projections (inline) */}
                      {(selectedMarketCap.trendData.risk.projected_trend_short || 
                        selectedMarketCap.trendData.risk.projected_trend_mid ||
                        selectedMarketCap.trendData.risk.projected_trend_long) && (
                        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          <Typography variant="body2" color="text.secondary" fontWeight="medium">
                            Projections:
                          </Typography>
                          
                          {selectedMarketCap.trendData.risk.projected_trend_short && (
                            <Chip 
                              label={`Short: ${selectedMarketCap.trendData.risk.projected_trend_short}`}
                              size="small"
                              sx={{
                                bgcolor: getSignalColor(selectedMarketCap.trendData.risk.projected_trend_short || ''),
                                color: 'white',
                                fontWeight: 'bold',
                                height: 20,
                                fontSize: '0.65rem'
                              }}
                            />
                          )}
                          
                          {selectedMarketCap.trendData.risk.projected_trend_mid && (
                            <Chip 
                              label={`Mid: ${selectedMarketCap.trendData.risk.projected_trend_mid}`}
                              size="small"
                              sx={{
                                bgcolor: getSignalColor(selectedMarketCap.trendData.risk.projected_trend_mid || ''),
                                color: 'white',
                                fontWeight: 'bold',
                                height: 20,
                                fontSize: '0.65rem'
                              }}
                            />
                          )}
                          
                          {selectedMarketCap.trendData.risk.projected_trend_long && (
                            <Chip 
                              label={`Long: ${selectedMarketCap.trendData.risk.projected_trend_long}`}
                              size="small"
                              sx={{
                                bgcolor: getSignalColor(selectedMarketCap.trendData.risk.projected_trend_long || ''),
                                color: 'white',
                                fontWeight: 'bold',
                                height: 20,
                                fontSize: '0.65rem'
                              }}
                            />
                          )}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                )}
                
                {/* Money Flow (full width) */}
                {selectedMarketCap.trendData?.moneyFlow && (
                  <Card variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                          Money Flow
                        </Typography>
                        <Box>
                          <Chip 
                            label={selectedMarketCap.trendData.moneyFlow.money_flow_rating || "N/A"}
                            size="small"
                            sx={{
                              bgcolor: getSignalColor(selectedMarketCap.trendData.moneyFlow.money_flow_rating || ''),
                              color: 'white',
                              fontWeight: 'bold'
                            }}
                          />
                          {/* Add historical money flow trend */}
                          {Object.keys(historicalReports).length > 0 && renderHistoricalTrendRating('Money Flow')}
                        </Box>
                      </Box>
                      
                      <Typography variant="body2" sx={{ mb: 1.5 }}>
                        {selectedMarketCap.trendData.moneyFlow.money_flow_detail || "No detailed money flow data available."}
                      </Typography>
                      
                      {/* Money Flow Projections (inline) */}
                      {(selectedMarketCap.trendData.moneyFlow.projected_trend_short || 
                        selectedMarketCap.trendData.moneyFlow.projected_trend_mid ||
                        selectedMarketCap.trendData.moneyFlow.projected_trend_long) && (
                        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          <Typography variant="body2" color="text.secondary" fontWeight="medium">
                            Projections:
                          </Typography>
                          
                          {selectedMarketCap.trendData.moneyFlow.projected_trend_short && (
                            <Chip 
                              label={`Short: ${selectedMarketCap.trendData.moneyFlow.projected_trend_short}`}
                              size="small"
                              sx={{
                                bgcolor: getSignalColor(selectedMarketCap.trendData.moneyFlow.projected_trend_short || ''),
                                color: 'white',
                                fontWeight: 'bold',
                                height: 20,
                                fontSize: '0.65rem'
                              }}
                            />
                          )}
                          
                          {selectedMarketCap.trendData.moneyFlow.projected_trend_mid && (
                            <Chip 
                              label={`Mid: ${selectedMarketCap.trendData.moneyFlow.projected_trend_mid}`}
                              size="small"
                              sx={{
                                bgcolor: getSignalColor(selectedMarketCap.trendData.moneyFlow.projected_trend_mid || ''),
                                color: 'white',
                                fontWeight: 'bold',
                                height: 20,
                                fontSize: '0.65rem'
                              }}
                            />
                          )}
                          
                          {selectedMarketCap.trendData.moneyFlow.projected_trend_long && (
                            <Chip 
                              label={`Long: ${selectedMarketCap.trendData.moneyFlow.projected_trend_long}`}
                              size="small"
                              sx={{
                                bgcolor: getSignalColor(selectedMarketCap.trendData.moneyFlow.projected_trend_long || ''),
                                color: 'white',
                                fontWeight: 'bold',
                                height: 20,
                                fontSize: '0.65rem'
                              }}
                            />
                          )}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                )}
              </Box>

              <Box sx={{ textAlign: 'right' }}>
                <Button 
                  variant="outlined" 
                  color="primary"
                  onClick={() => setSelectedMarketCap(null)}
                >
                  Back to All Market Caps
                </Button>
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>
      
      {/* Market Caps allocation summary */}
      <Paper sx={{ p: 3, mt: 4 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <PieChart sx={{ mr: 1, color: theme.palette.custom.iconSecondary }} />
          Recommended Market Cap Allocation Summary
        </Typography>
        
        <TableContainer sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Market Cap</TableCell>
                <TableCell>Rating</TableCell>
                <TableCell>Trend</TableCell>
                <TableCell>Performance</TableCell>
                <TableCell>Risk</TableCell>
                <TableCell>Money Flow</TableCell>
                <TableCell>Ideal %</TableCell>
                <TableCell>Allocation</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {marketCapsWithTrendData.map((marketCap: MarketCapWithTrendData, index: number) => (
                <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    {marketCap.Classification.replace('Market Cap: ', '')}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={marketCap.Rating}
                      size="small"
                      sx={{ 
                        bgcolor: getSignalColor(marketCap.Rating),
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.75rem'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {getTrendIconAndColor(marketCap.Trend).icon}
                      <Typography variant="body2" sx={{ ml: 0.5 }}>
                        {marketCap.Trend}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {marketCap.trendData?.performance?.performance_rating || "N/A"}
                  </TableCell>
                  <TableCell>
                    {marketCap.trendData?.risk?.risk_rating || "N/A"}
                  </TableCell>
                  <TableCell>
                    {marketCap.trendData?.moneyFlow?.money_flow_rating || "N/A"}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    {marketCap.Allocations?.ideal || 0}%
                  </TableCell>
                  <TableCell sx={{ width: '15%' }}>
                    <AllocationBar 
                      variant="determinate" 
                      value={marketCap.Allocations?.ideal || 0} 
                      max={marketCap.Allocations?.max || 10}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Top Bullish Market Caps
            </Typography>
            <List dense>
              {marketCapsWithTrendData
                .filter((s: MarketCapWithTrendData) => s.Rating.includes('BULLISH'))
                .slice(0, 3)
                .map((s: MarketCapWithTrendData, idx: number) => (
                  <ListItem key={idx}>
                    <ListItemIcon>
                      <CheckCircle sx={{ color: getSignalColor(s.Rating) }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={s.Classification.replace('Market Cap: ', '')}
                      secondary={`Perf: ${s.trendData?.performance?.performance_rating || 'N/A'} | Risk: ${s.trendData?.risk?.risk_rating || 'N/A'} | Flow: ${s.trendData?.moneyFlow?.money_flow_rating || 'N/A'}`}
                    />
                  </ListItem>
                ))
              }
            </List>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Top Bearish Market Caps
            </Typography>
            <List dense>
              {marketCapsWithTrendData
                .filter((s: MarketCapWithTrendData) => s.Rating.includes('BEARISH'))
                .slice(0, 3)
                .map((s: MarketCapWithTrendData, idx: number) => (
                  <ListItem key={idx}>
                    <ListItemIcon>
                      <Cancel sx={{ color: getSignalColor(s.Rating) }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={s.Classification.replace('Market Cap: ', '')}
                      secondary={`Perf: ${s.trendData?.performance?.performance_rating || 'N/A'} | Risk: ${s.trendData?.risk?.risk_rating || 'N/A'} | Flow: ${s.trendData?.moneyFlow?.money_flow_rating || 'N/A'}`}
                    />
                  </ListItem>
                ))
              }
            </List>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default MarketCapsView;
